import {
  type FC,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useBlocNext } from "@blac/react";
import { ToolCallSection } from "@9amhealth/shared";
import Link from "src/ui/components/Link/Link";
import LeapChatCubit, { type LeapChatMessage } from "./LeapChatCubit";
import ChatComposer from "./ChatComposer";
import {
  LeapChatContainer,
  LeapChatViewport,
  MessageListContent,
  AssistantMessageRoot,
  AssistantAvatar,
  AssistantMessageBody,
  AssistantMessageContent,
  UserMessageRoot,
  UserMessageContent,
  MarkdownWrapper,
  MessageFooter,
  FooterName,
  ComposerCardArea
} from "./leapChat.styles";

const markdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <Link to={href}>{children}</Link>
  )
};

const MarkdownContent: FC<{ text: string }> = memo(({ text }) => (
  <MarkdownWrapper>
    <ReactMarkdown components={markdownComponents}>{text}</ReactMarkdown>
  </MarkdownWrapper>
));

const AssistantFooter: FC = () => (
  <MessageFooter>
    <FooterName>Leap</FooterName> · Health Assistant
  </MessageFooter>
);

const UserBubble: FC<{ message: LeapChatMessage }> = memo(({ message }) => (
  <UserMessageRoot>
    <UserMessageContent>
      <MarkdownContent text={message.text} />
    </UserMessageContent>
  </UserMessageRoot>
));

const AssistantBubble: FC<{
  message: LeapChatMessage;
  onLayoutChange: () => void;
}> = memo(({ message, onLayoutChange }) => {
  useLayoutEffect(() => {
    onLayoutChange();
  }, [onLayoutChange(), message.text]);

  return (
    <AssistantMessageRoot>
      <AssistantAvatar>
        <img src="/9amAvatar.png" alt="9am.health" />
      </AssistantAvatar>
      <AssistantMessageBody>
        <AssistantMessageContent>
          <ToolCallSection toolCalls={message.toolCalls ?? []} />
          <MarkdownContent text={message.text} />
        </AssistantMessageContent>
        <AssistantFooter />
      </AssistantMessageBody>
    </AssistantMessageRoot>
  );
});

const ConversationDetailView: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [{ currentMessages, isInitializing, activeConversationId }, cubit] =
    useBlocNext(LeapChatCubit);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id && id !== activeConversationId) {
      void cubit.loadConversation(id);
    }
  }, [id, cubit]);

  useEffect(() => {
    cubit.setDomElements({
      scrollEl: scrollRef.current,
      contentEl: contentRef.current
    });
  }, [cubit, scrollRef.current, contentRef.current]);

  useEffect(() => {
    if (!isInitializing && currentMessages.length > 0) {
      cubit.scrollToBottom();
    }
  }, [isInitializing]);

  const handleSend = useCallback(() => {
    cubit.submitInput();
  }, [cubit]);

  if (isInitializing) {
    return <LeapChatContainer />;
  }

  return (
    <LeapChatContainer>
      <LeapChatViewport ref={scrollRef}>
        <MessageListContent ref={contentRef}>
          <div style={{ flexGrow: 1 }} />
          {currentMessages.map((msg) =>
            msg.role === "user" ? (
              <UserBubble key={msg.id} message={msg} />
            ) : (
              <AssistantBubble
                key={msg.id}
                message={msg}
                onLayoutChange={cubit.restoreScrollIfNeeded}
              />
            )
          )}
        </MessageListContent>
        <ComposerCardArea>
          <ChatComposer
            onSend={handleSend}
            placeholder="Message your care team…"
          />
        </ComposerCardArea>
      </LeapChatViewport>
    </LeapChatContainer>
  );
};

export default ConversationDetailView;
